import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Building,
  Building2,
  Calculator,
  CheckCircle2,
  Clock,
  FileSearch,
  Gauge,
  GitCompareArrows,
  HardHat,
  HelpCircle,
  Home,
  Landmark,
  Linkedin,
  Mail,
  MapPin,
  MessageCircle,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Tractor,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../../components/layout/Container';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Stepper } from '../../components/ui/stepper';
import { Reveal } from '../../components/motion/Reveal';
import { TrustBar } from '../../components/conversion/TrustBar';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { toast } from '../../components/ui/toast';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { cn } from '../../lib/cn';
import { ROUTES } from '@shared/constants/routes';
import { LEGAL } from '@shared/constants/legal-info';
import type { Segment } from '@shared/constants/segments';
import type { Intent } from '@shared/constants/intents';

// ════════════════════════════════════════════════════════════════
// Configuration éditoriale
// ════════════════════════════════════════════════════════════════

type ToneKey = 'brand' | 'lavender' | 'sage' | 'lavender-700' | 'brand-700' | 'accent';

interface AudienceOption {
  id:
    | 'particulier'
    | 'pro'
    | 'entreprise'
    | 'collectivite'
    | 'copropriete'
    | 'artisan'
    | 'agriculteur';
  label: string;
  hint: string;
  icon: LucideIcon;
  segment: Segment;
  defaultIntent: Intent | 'autre';
  tone: ToneKey;
  highlight?: boolean;
  formEyebrow: string;
  formFootnote: string;
  showCompany: boolean;
  companyLabel: string;
}

const AUDIENCE_OPTIONS: AudienceOption[] = [
  {
    id: 'particulier',
    label: 'Particulier',
    hint: 'Propriétaire ou bailleur',
    icon: Home,
    segment: 'particulier',
    defaultIntent: 'renover',
    tone: 'brand',
    formEyebrow: 'Parlons de votre projet',
    formFootnote: 'Diagnostic et étude des aides 100 % gratuits.',
    showCompany: false,
    companyLabel: 'Organisation',
  },
  {
    id: 'pro',
    label: 'Pro, TPE, PME',
    hint: 'Commerce, restaurant, agence, profession libérale',
    icon: Building2,
    segment: 'pro',
    defaultIntent: 'comparer',
    tone: 'lavender',
    formEyebrow: 'Audit factures gratuit',
    formFootnote: 'Diagnostic sous 48 h, aucun engagement de changement.',
    showCompany: true,
    companyLabel: 'Entreprise',
  },
  {
    id: 'entreprise',
    label: 'ETI, Grand compte',
    hint: 'Industriel, multi-sites, foncière',
    icon: Building2,
    segment: 'entreprise',
    defaultIntent: 'conformite',
    tone: 'sage',
    formEyebrow: 'Étude chiffrée, confidentialité garantie',
    formFootnote: 'Référent dédié, premiers résultats sous 15 jours.',
    showCompany: true,
    companyLabel: 'Société',
  },
  {
    id: 'collectivite',
    label: 'Collectivité',
    hint: 'Mairie, EPCI, bailleur social',
    icon: Landmark,
    segment: 'entreprise',
    defaultIntent: 'conformite',
    tone: 'lavender-700',
    formEyebrow: 'Pré-étude offerte, conforme commande publique',
    formFootnote: 'Référencé UGAP, note pour convocation incluse.',
    showCompany: true,
    companyLabel: 'Collectivité',
  },
  {
    id: 'copropriete',
    label: 'Copropriété, Syndic',
    hint: 'Syndic pro, conseil syndical',
    icon: Building,
    segment: 'syndic',
    defaultIntent: 'renover',
    tone: 'brand-700',
    formEyebrow: 'Présentation AG offerte',
    formFootnote: 'Projet de résolution rédigé, vote positif visé.',
    showCompany: true,
    companyLabel: 'Cabinet de syndic',
  },
  {
    id: 'artisan',
    label: 'Artisan RGE',
    hint: 'Primes pour vos chantiers, zéro avance',
    icon: HardHat,
    segment: 'pro',
    defaultIntent: 'cee',
    tone: 'accent',
    highlight: true,
    formEyebrow: 'Devenir partenaire primes énergie',
    formFootnote: 'Zéro avance, paiement sous 30 à 60 jours, sans engagement de volume.',
    showCompany: true,
    companyLabel: 'Entreprise (raison sociale)',
  },
  {
    id: 'agriculteur',
    label: 'Agriculteur',
    hint: 'Exploitation, élevage, viticulture, céréales',
    icon: Tractor,
    segment: 'agri',
    defaultIntent: 'cee',
    tone: 'accent',
    formEyebrow: 'Diagnostic exploitation gratuit',
    formFootnote: 'Primes exploitation agricole, aides régionales.',
    showCompany: true,
    companyLabel: 'Exploitation',
  },
];

interface IntentOption {
  id: Intent | 'autre';
  label: string;
  hint: string;
  icon: LucideIcon;
}

const INTENT_OPTIONS: IntentOption[] = [
  {
    id: 'cee',
    label: 'Primes énergie',
    hint: 'Valoriser un chantier, estimer vos primes',
    icon: Zap,
  },
  {
    id: 'comparer',
    label: 'Comparer mes contrats énergie',
    hint: 'Audit factures gaz et électricité, tous fournisseurs',
    icon: GitCompareArrows,
  },
  {
    id: 'renover',
    label: 'Aides rénovation',
    hint: "Aides de l'État, prêt sans intérêt, prime énergie, cumul",
    icon: Wrench,
  },
  {
    id: 'conformite',
    label: 'Obligation tertiaire',
    hint: "Trajectoire de réduction 2030, plan d'actions",
    icon: Gauge,
  },
  {
    id: 'autre',
    label: 'Autre demande',
    hint: 'Partenariat presse, recrutement, juridique…',
    icon: HelpCircle,
  },
];

const TONE_STYLES: Record<
  ToneKey,
  { bg: string; ring: string; iconBg: string; iconText: string; ringActive: string }
> = {
  brand: {
    bg: 'bg-brand-50/50',
    ring: 'ring-brand-200/60',
    iconBg: 'bg-brand-100',
    iconText: 'text-brand-700',
    ringActive: 'ring-brand-500/40',
  },
  lavender: {
    bg: 'bg-lavender-50/50',
    ring: 'ring-lavender-200/60',
    iconBg: 'bg-lavender-100',
    iconText: 'text-lavender-700',
    ringActive: 'ring-lavender-500/40',
  },
  sage: {
    bg: 'bg-sage-100/70',
    ring: 'ring-stone-200/60',
    iconBg: 'bg-sage-200/80',
    iconText: 'text-sage-500',
    ringActive: 'ring-sage-500/40',
  },
  'lavender-700': {
    bg: 'bg-lavender-50/40',
    ring: 'ring-lavender-300/60',
    iconBg: 'bg-lavender-100',
    iconText: 'text-lavender-800',
    ringActive: 'ring-lavender-700/40',
  },
  'brand-700': {
    bg: 'bg-brand-50/40',
    ring: 'ring-brand-300/60',
    iconBg: 'bg-brand-100',
    iconText: 'text-brand-800',
    ringActive: 'ring-brand-700/40',
  },
  accent: {
    bg: 'bg-accent-50/60',
    ring: 'ring-accent-200/70',
    iconBg: 'bg-accent-100',
    iconText: 'text-accent-800',
    ringActive: 'ring-accent-500/45',
  },
};

const SELF_SERVICE = [
  {
    title: 'Diagnostic primes en 60 sec',
    desc: 'Estimation chiffrée immédiate, sans inscription.',
    href: ROUTES.diagnostic,
    icon: ScanLine,
    tone: 'brand' as ToneKey,
  },
  {
    title: "Calculateur d'aides rénovation",
    desc: 'Toutes les aides cumulées automatiquement.',
    href: ROUTES.renovation,
    icon: Calculator,
    tone: 'lavender' as ToneKey,
  },
  {
    title: "Comparateur d'offres énergie",
    desc: "Courtier indépendant des fournisseurs d'énergie.",
    href: ROUTES.comparateur,
    icon: GitCompareArrows,
    tone: 'sage' as ToneKey,
  },
  {
    title: 'Espace artisan',
    desc: 'Primes énergie, zéro avance, paiement 30-60 jours.',
    href: ROUTES.artisans,
    icon: HardHat,
    tone: 'accent' as ToneKey,
    highlight: true,
  },
];

const FAQ: Array<{ q: string; a: string }> = [
  {
    q: 'Sous quel délai répondez-vous ?',
    a: '24 heures maximum, en moyenne moins de 6 h en journée. Pour les grands comptes et collectivités, un référent dédié vous rappelle dans la demi-journée.',
  },
  {
    q: 'Le premier échange est-il vraiment gratuit ?',
    a: 'Oui. Diagnostic, audit et pré-étude sont gratuits et sans engagement. Aucun frais sans votre accord écrit.',
  },
  {
    q: 'Mes données sont-elles confidentielles ?',
    a: 'Hébergement en France, conforme RGPD. Aucune revente, aucune transmission à des tiers. Un accord de confidentialité est disponible pour les grands comptes et collectivités.',
  },
  {
    q: 'Vais-je être démarché ?',
    a: "Non. Nous vous recontactons uniquement si vous l'avez demandé via le formulaire.",
  },
  {
    q: 'Si vous ne trouvez aucune économie ?',
    a: "Nous vous le disons franchement et le service s'arrête là, sans frais. Notre modèle tient sur la valeur apportée, pas sur la signature à tout prix.",
  },
  {
    q: 'Comment fonctionne votre indépendance ?',
    a: 'Sur le courtage énergie, aucun lien avec un fournisseur. Sur les travaux et primes, nos partenariats sont transparents et détaillés dans nos conditions générales.',
  },
];

const QUICK_CHANNELS = [
  {
    icon: Mail,
    label: 'Par email',
    value: 'contact@solutionecofrance.fr',
    href: 'mailto:contact@solutionecofrance.fr',
    sub: 'Réponse sous 24 h ouvrées',
  },
  {
    icon: Linkedin,
    label: 'LinkedIn',
    value: 'Solution Éco France',
    href: 'https://www.linkedin.com/company/solution-eco-france',
    sub: 'Veille marché & actualités',
    external: true,
  },
  {
    icon: MapPin,
    label: 'Adresse',
    value: `${LEGAL.headquarters.street}, ${LEGAL.headquarters.postalCode} ${LEGAL.headquarters.city}`,
    href: null,
    sub: 'SAS · SIREN 982 431 033',
  },
] as const;

const WIZARD_STEPS = [
  { id: 'profile', label: 'Profil' },
  { id: 'intent', label: 'Besoin' },
  { id: 'details', label: 'Coordonnées' },
] as const;

// ════════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════════

function findAudienceFromSource(source: string | null): AudienceOption | null {
  if (!source) return null;
  const map: Record<string, AudienceOption['id']> = {
    particulier: 'particulier',
    particuliers: 'particulier',
    pro: 'pro',
    professionnels: 'pro',
    professionnel: 'pro',
    entreprise: 'entreprise',
    entreprises: 'entreprise',
    eti: 'entreprise',
    collectivite: 'collectivite',
    collectivites: 'collectivite',
    copropriete: 'copropriete',
    coproprietes: 'copropriete',
    syndic: 'copropriete',
    artisan: 'artisan',
    artisans: 'artisan',
    agriculteur: 'agriculteur',
    agriculteurs: 'agriculteur',
    agri: 'agriculteur',
  };
  const key = source.toLowerCase().trim();
  const audienceId = map[key];
  return AUDIENCE_OPTIONS.find((a) => a.id === audienceId) ?? null;
}

function findIntentById(id: string | null): IntentOption | null {
  if (!id) return null;
  return INTENT_OPTIONS.find((opt) => opt.id === (id as IntentOption['id'])) ?? null;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  consentMarketing: boolean;
}

const INITIAL_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  message: '',
  consentMarketing: false,
};

type StepIndex = 0 | 1 | 2;

// ════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════

export default function ContactPage() {
  const [stepIndex, setStepIndex] = useState<StepIndex>(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [audience, setAudience] = useState<AudienceOption | null>(null);
  const [intent, setIntent] = useState<IntentOption | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ id: string } | null>(null);
  const reducedMotion = useReducedMotion();

  // Pré-remplissage depuis l'URL
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const preset =
      findAudienceFromSource(params.get('source')) ??
      findAudienceFromSource(params.get('audience'));
    const intentPreset =
      findIntentById(params.get('intent')) ??
      (preset ? findIntentById(preset.defaultIntent) : null);

    if (preset && intentPreset) {
      setAudience(preset);
      setIntent(intentPreset);
      setStepIndex(2);
      setDirection(1);
    } else if (preset) {
      setAudience(preset);
      setIntent(intentPreset);
      setStepIndex(1);
      setDirection(1);
    } else if (intentPreset) {
      setIntent(intentPreset);
    }
  }, []);

  const goToStep = (next: StepIndex) => {
    setDirection(next > stepIndex ? 1 : -1);
    setStepIndex(next);
  };

  const updateField =
    <K extends keyof FormState>(key: K) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value =
        e.target instanceof HTMLInputElement && e.target.type === 'checkbox'
          ? e.target.checked
          : e.target.value;
      setForm((prev) => ({ ...prev, [key]: value as FormState[K] }));
    };

  const handleSelectAudience = (option: AudienceOption) => {
    setAudience(option);
    if (!intent) {
      const next = findIntentById(option.defaultIntent);
      if (next) setIntent(next);
    }
    // Petit délai pour laisser voir la confirmation visuelle
    window.setTimeout(() => goToStep(1), 220);
  };

  const handleSelectIntent = (option: IntentOption) => {
    setIntent(option);
    window.setTimeout(() => goToStep(2), 220);
  };

  const isEmailValid = useMemo(() => /\S+@\S+\.\S+/.test(form.email.trim()), [form.email]);
  const canProceed = useMemo(() => {
    if (stepIndex === 0) return audience !== null;
    if (stepIndex === 1) return intent !== null;
    return Boolean(form.firstName.trim() && form.lastName.trim() && isEmailValid);
  }, [stepIndex, audience, intent, form.firstName, form.lastName, isEmailValid]);

  const handleSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (submitting) return;

    if (!isEmailValid) {
      toast.error('Email invalide', { description: 'Merci d’indiquer une adresse correcte.' });
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error('Nom et prénom requis');
      return;
    }

    const apiIntent: Intent = intent && intent.id !== 'autre' ? (intent.id as Intent) : 'inconnu';

    const messageWithContext = (() => {
      const parts: string[] = [];
      if (intent?.id === 'autre') parts.push('[Sujet : autre demande]');
      else if (intent) parts.push(`[Sujet : ${intent.label}]`);
      if (form.company.trim()) parts.push(`Société : ${form.company.trim()}`);
      if (form.message.trim()) parts.push(form.message.trim());
      return parts.join('\n');
    })();

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      message: messageWithContext || undefined,
      consentMarketing: form.consentMarketing,
      source: 'contact' as const,
      segment: audience?.segment ?? 'inconnu',
      intent: apiIntent,
    };

    setSubmitting(true);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Erreur ${res.status}`);
      }
      const data = (await res.json()) as { id: string };
      setSuccess({ id: data.id });
      toast.success('Message envoyé', {
        description: 'Nous revenons vers vous sous 24 h ouvrées.',
      });
    } catch (err) {
      toast.error('Envoi impossible', {
        description: err instanceof Error ? err.message : 'Réessayez dans un instant.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formCopy = useMemo(() => {
    if (!audience) {
      return {
        eyebrow: 'Parlons de votre projet',
        footnote: 'Réponse sous 24 h ouvrées. Diagnostic et audit toujours gratuits.',
      };
    }
    return { eyebrow: audience.formEyebrow, footnote: audience.formFootnote };
  }, [audience]);

  const handlePrimary = () => {
    if (stepIndex === 2) {
      void handleSubmit();
      return;
    }
    if (canProceed) goToStep((stepIndex + 1) as StepIndex);
  };

  const handleSecondary = () => {
    if (stepIndex === 0) return;
    goToStep((stepIndex - 1) as StepIndex);
  };

  return (
    <div className="bg-paper">
      {/* ─────────────── HERO COMPACT ─────────────── */}
      <section className="relative overflow-hidden border-b border-stone-200/70 bg-stone-50/60 py-12 md:py-14">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 right-1/4 h-56 w-56 rounded-full bg-brand-500/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-accent-500/10 blur-3xl"
        />
        <Container className="relative max-w-5xl">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
              <div className="max-w-2xl">
                <p className="eyebrow">
                  <Sparkles className="h-3 w-3 text-brand-600" strokeWidth={2} />
                  <span>Nous contacter · 24 h ouvrées</span>
                </p>
                <h1 className="h-hero mt-3">
                  Le bon expert pour votre besoin,{' '}
                  <span className="text-gradient-soft">en trois étapes.</span>
                </h1>
                <p className="mt-3 max-w-xl text-[14.5px] leading-relaxed text-stone-600">
                  Dites-nous qui vous êtes et ce que vous cherchez : on vous oriente vers le bon
                  expert.
                </p>
              </div>
              <ul className="hidden shrink-0 flex-col gap-1.5 text-[12px] text-stone-500 md:flex">
                <li className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-brand-600" strokeWidth={1.8} />
                  Réponse écrite sous 24 h ouvrées
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-brand-600" strokeWidth={1.8} />
                  RGPD strict · hébergement France
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" strokeWidth={1.8} />
                  Diagnostic et audit toujours gratuits
                </li>
              </ul>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* ─────────────── WIZARD + SIDEBAR ─────────────── */}
      <section className="bg-paper py-10 md:py-14">
        <Container className="max-w-5xl">
          <div className="grid gap-6 lg:grid-cols-[1.55fr_1fr]">
            <Reveal>
              <div className="rounded-2xl border border-stone-200 bg-paper shadow-soft">
                {/* En-tête : stepper + label étape */}
                <div className="border-b border-stone-200/70 px-5 py-4 sm:px-7 sm:py-5">
                  {success ? (
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="eyebrow">Étape 4 sur 4 · Confirmé</p>
                        <p className="mt-1 font-display text-[14px] font-semibold text-ink">
                          Demande enregistrée
                        </p>
                      </div>
                      <Badge variant="default" size="md" className="gap-1.5">
                        <CheckCircle2 className="h-3 w-3" strokeWidth={2.4} />
                        Reçu
                      </Badge>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-4">
                        <p className="eyebrow">
                          Étape {stepIndex + 1} sur 3 · {WIZARD_STEPS[stepIndex].label}
                        </p>
                        {audience && stepIndex >= 1 && (
                          <button
                            type="button"
                            onClick={() => goToStep(0)}
                            className="inline-flex items-center gap-1 text-[12px] font-medium text-stone-500 underline-offset-2 hover:text-ink hover:underline"
                          >
                            Changer
                          </button>
                        )}
                      </div>
                      <Stepper
                        steps={WIZARD_STEPS.map((s) => ({ id: s.id, label: s.label }))}
                        currentStep={stepIndex}
                        variant="compact"
                      />
                    </div>
                  )}
                </div>

                {/* Contenu animé des étapes */}
                <div className="relative px-5 py-7 sm:px-7 sm:py-8">
                  <AnimatePresence mode="wait" custom={direction} initial={false}>
                    {success ? (
                      <StepShell key="success" direction={direction} reducedMotion={reducedMotion}>
                        <SuccessPanel
                          leadId={success.id}
                          audience={audience}
                          intent={intent}
                          onReset={() => {
                            setSuccess(null);
                            setForm(INITIAL_FORM);
                            setStepIndex(0);
                          }}
                        />
                      </StepShell>
                    ) : stepIndex === 0 ? (
                      <StepShell key="profile" direction={direction} reducedMotion={reducedMotion}>
                        <StepHeader
                          eyebrow="Qui êtes-vous ?"
                          title="On adapte le bon expert à votre profil."
                          subtitle="7 catégories couvrent l’ensemble de nos publics. Vous ne tranchez pas ? Choisissez le plus proche, on affinera ensemble."
                        />
                        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {AUDIENCE_OPTIONS.map((opt) => {
                            const Icon = opt.icon;
                            const tone = TONE_STYLES[opt.tone];
                            const active = audience?.id === opt.id;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => handleSelectAudience(opt)}
                                aria-pressed={active}
                                className={cn(
                                  'group relative flex h-full w-full flex-col items-start gap-3 rounded-xl border bg-paper p-4 text-left transition-all duration-200 ease-out',
                                  'hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated',
                                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 focus-visible:ring-offset-2',
                                  active
                                    ? cn(
                                        'border-transparent ring-2 ring-offset-2',
                                        tone.ringActive,
                                        tone.bg,
                                      )
                                    : 'border-stone-200',
                                )}
                              >
                                <div
                                  className={cn(
                                    'flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset',
                                    tone.iconBg,
                                    tone.ring,
                                  )}
                                >
                                  <Icon
                                    className={cn('h-4 w-4', tone.iconText)}
                                    strokeWidth={1.8}
                                  />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-display text-[14.5px] font-semibold text-ink">
                                      {opt.label}
                                    </p>
                                    {opt.highlight && (
                                      <Badge variant="accent" size="sm" className="px-1.5 py-0">
                                        Recommandé
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="mt-1 text-[12.5px] leading-snug text-stone-500">
                                    {opt.hint}
                                  </p>
                                </div>
                                {active && (
                                  <div
                                    className={cn(
                                      'absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full',
                                      tone.iconBg,
                                      tone.iconText,
                                    )}
                                    aria-hidden
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </StepShell>
                    ) : stepIndex === 1 ? (
                      <StepShell key="intent" direction={direction} reducedMotion={reducedMotion}>
                        <StepHeader
                          eyebrow="Votre besoin"
                          title={
                            audience
                              ? `${audience.label} — sur quel sujet pouvons-nous être utiles ?`
                              : 'Sur quel sujet pouvons-nous vous être utiles ?'
                          }
                          subtitle="Une seule réponse. Votre expert SEF couvre les trois pôles, vous pourrez changer d’avis."
                        />
                        <div className="mt-6 grid gap-3 sm:grid-cols-2">
                          {INTENT_OPTIONS.map((opt) => {
                            const Icon = opt.icon;
                            const active = intent?.id === opt.id;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => handleSelectIntent(opt)}
                                aria-pressed={active}
                                className={cn(
                                  'group flex h-full w-full items-start gap-3 rounded-xl border bg-paper p-4 text-left transition-all duration-200 ease-out',
                                  'hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated',
                                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 focus-visible:ring-offset-2',
                                  active
                                    ? 'border-brand-500/60 bg-brand-50/40 ring-2 ring-brand-500/30 ring-offset-2'
                                    : 'border-stone-200',
                                )}
                              >
                                <div
                                  className={cn(
                                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                                    active
                                      ? 'bg-brand-600 text-white'
                                      : 'bg-stone-100 text-stone-600',
                                  )}
                                >
                                  <Icon className="h-4 w-4" strokeWidth={1.8} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="font-display text-[14px] font-semibold text-ink">
                                    {opt.label}
                                  </p>
                                  <p className="mt-1 text-[12px] leading-snug text-stone-500">
                                    {opt.hint}
                                  </p>
                                </div>
                                {active && (
                                  <CheckCircle2
                                    className="mt-0.5 h-4 w-4 shrink-0 text-brand-600"
                                    strokeWidth={2.5}
                                  />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </StepShell>
                    ) : (
                      <StepShell key="details" direction={direction} reducedMotion={reducedMotion}>
                        <StepHeader
                          eyebrow={formCopy.eyebrow}
                          title="Vos coordonnées"
                          subtitle="Plus le contexte est précis, plus la première réponse est utile."
                        />

                        {(audience || intent) && (
                          <div className="mt-4 flex flex-wrap items-center gap-1.5">
                            {audience && (
                              <Badge variant="outline" size="sm">
                                <MessageCircle className="h-3 w-3" strokeWidth={2} />
                                {audience.label}
                              </Badge>
                            )}
                            {intent && (
                              <Badge variant="outline" size="sm">
                                <intent.icon className="h-3 w-3" strokeWidth={2} />
                                {intent.label}
                              </Badge>
                            )}
                          </div>
                        )}

                        <form
                          className="mt-6 grid gap-4 sm:grid-cols-2"
                          onSubmit={(e) => {
                            e.preventDefault();
                            void handleSubmit(e);
                          }}
                          noValidate
                        >
                          <FieldText
                            label="Prénom"
                            name="firstName"
                            autoComplete="given-name"
                            required
                            value={form.firstName}
                            onChange={updateField('firstName')}
                            disabled={submitting}
                          />
                          <FieldText
                            label="Nom"
                            name="lastName"
                            autoComplete="family-name"
                            required
                            value={form.lastName}
                            onChange={updateField('lastName')}
                            disabled={submitting}
                          />
                          <FieldText
                            label="Email"
                            type="email"
                            name="email"
                            autoComplete="email"
                            required
                            className="sm:col-span-2"
                            value={form.email}
                            onChange={updateField('email')}
                            disabled={submitting}
                            error={
                              form.email.length > 3 && !isEmailValid
                                ? 'Format d’email invalide'
                                : undefined
                            }
                          />
                          <FieldText
                            label="Téléphone (optionnel)"
                            type="tel"
                            name="phone"
                            autoComplete="tel"
                            hint="Pour un rappel sous 24 h ouvrées si vous le souhaitez."
                            value={form.phone}
                            onChange={updateField('phone')}
                            disabled={submitting}
                          />
                          <FieldText
                            label={audience?.companyLabel ?? 'Organisation'}
                            name="company"
                            autoComplete="organization"
                            hint={
                              audience?.id === 'artisan'
                                ? 'Vérification automatique de votre RGE sur le registre officiel.'
                                : audience?.id === 'entreprise'
                                  ? 'Pour briefer en amont notre référent dédié.'
                                  : 'Optionnel'
                            }
                            value={form.company}
                            onChange={updateField('company')}
                            disabled={submitting}
                          />
                          <div className="sm:col-span-2">
                            <label
                              htmlFor="contact-message"
                              className="flex items-center gap-1 text-[13px] font-medium text-ink"
                            >
                              Votre message
                            </label>
                            <textarea
                              id="contact-message"
                              name="message"
                              rows={4}
                              value={form.message}
                              onChange={updateField('message')}
                              disabled={submitting}
                              placeholder={getMessagePlaceholder(audience, intent)}
                              className="mt-1.5 block w-full rounded-xl border border-stone-200 bg-paper px-3 py-2.5 text-[14px] leading-relaxed text-ink transition-colors placeholder:text-stone-400 focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                            />
                          </div>

                          <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-stone-200 bg-stone-50/40 p-3 text-[12.5px] text-stone-600 sm:col-span-2">
                            <input
                              type="checkbox"
                              name="consentMarketing"
                              checked={form.consentMarketing}
                              onChange={updateField('consentMarketing')}
                              disabled={submitting}
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-stone-300 text-brand-600 focus-visible:ring-2 focus-visible:ring-brand-500/30"
                            />
                            <span className="leading-relaxed">
                              J'accepte de recevoir occasionnellement des informations utiles à mon
                              projet (aides, évolutions réglementaires). Désabonnement en 1 clic.
                              Voir notre{' '}
                              <a
                                href={ROUTES.legal.confidentialite}
                                className="font-semibold text-brand-700 underline-offset-2 hover:underline"
                              >
                                politique de confidentialité
                              </a>
                              .
                            </span>
                          </label>
                          {/* submit caché pour permettre Enter */}
                          <button type="submit" className="sr-only" tabIndex={-1} aria-hidden>
                            Envoyer
                          </button>
                        </form>
                      </StepShell>
                    )}
                  </AnimatePresence>
                </div>

                {/* Footer de navigation (caché en mode succès) */}
                {!success && (
                  <div className="flex flex-col gap-3 border-t border-stone-200/70 bg-stone-50/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="md"
                        onClick={handleSecondary}
                        disabled={stepIndex === 0 || submitting}
                        className="gap-1.5"
                      >
                        <ArrowLeft className="h-4 w-4" strokeWidth={2.2} />
                        Précédent
                      </Button>
                      <p className="hidden text-[11.5px] text-stone-500 sm:block">
                        <ShieldCheck
                          className="-mt-0.5 mr-1 inline h-3 w-3 text-brand-600"
                          strokeWidth={2}
                        />
                        {formCopy.footnote}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="brand"
                      size="lg"
                      onClick={handlePrimary}
                      disabled={!canProceed || submitting}
                      aria-busy={submitting}
                      className="gap-1.5"
                    >
                      {submitting ? (
                        <>
                          <Spinner /> Envoi en cours…
                        </>
                      ) : stepIndex === 2 ? (
                        <>
                          Envoyer ma demande
                          <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
                        </>
                      ) : (
                        <>
                          Continuer
                          <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
                        </>
                      )}
                    </Button>
                  </div>
                )}
                {!success && (
                  <div className="border-t border-stone-200/70 px-5 py-3 sm:px-7">
                    <TrustBar variant="inline" max={4} className="justify-start" />
                  </div>
                )}
              </div>
            </Reveal>

            {/* SIDEBAR */}
            <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
              <Reveal>
                <div className="rounded-2xl border border-stone-200 bg-stone-50/40 p-5">
                  <p className="eyebrow">Canaux directs</p>
                  <h3 className="mt-2 font-display text-[15px] font-semibold leading-tight text-ink">
                    Vous préférez nous joindre autrement&nbsp;?
                  </h3>
                  <ul className="mt-4 space-y-2">
                    {QUICK_CHANNELS.map((ch) => {
                      const Icon = ch.icon;
                      const content = (
                        <span className="group flex items-start gap-3 rounded-xl border border-stone-200/70 bg-paper p-3 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated">
                          <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50">
                            <Icon className="h-3.5 w-3.5 text-brand-700" strokeWidth={1.8} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                              {ch.label}
                            </span>
                            <span className="block truncate text-[13px] font-medium text-ink">
                              {ch.value}
                            </span>
                            <span className="block text-[11px] text-stone-500">{ch.sub}</span>
                          </span>
                          {ch.href && (
                            <ArrowUpRight
                              className="mt-0.5 h-3.5 w-3.5 text-stone-300 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-ink"
                              strokeWidth={2}
                            />
                          )}
                        </span>
                      );
                      return (
                        <li key={ch.label}>
                          {ch.href ? (
                            <a
                              href={ch.href}
                              {...('external' in ch && ch.external
                                ? { target: '_blank', rel: 'noopener noreferrer' }
                                : {})}
                              className="block"
                            >
                              {content}
                            </a>
                          ) : (
                            content
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={0.05}>
                <div className="rounded-2xl border border-stone-200 bg-paper p-5">
                  <div className="flex items-center gap-2">
                    <span aria-hidden className="relative inline-flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-glow-pulse rounded-full bg-brand-500/60" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500" />
                    </span>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
                      Horaires de réponse
                    </p>
                  </div>
                  <p className="mt-3 text-[13px] leading-relaxed text-stone-600">
                    <span className="font-semibold text-ink">Lundi → Vendredi</span>, 9 h – 18 h
                    <br />
                    Réponse écrite sous 24 h, rappel téléphonique sous 4 h sur demande.
                  </p>
                  <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50/60 p-3 text-[11.5px] leading-relaxed text-stone-500">
                    Pour les <strong className="text-ink">artisans RGE</strong> en suivi de dossier,
                    l’assistance dédiée est joignable via votre référent Solution Éco France
                    (coordonnées communiquées à l’activation du partenariat).
                  </div>
                </div>
              </Reveal>
            </aside>
          </div>
        </Container>
      </section>

      {/* ─────────────── SELF-SERVICE ─────────────── */}
      <section className="border-t border-stone-200/70 bg-stone-50/50 py-16">
        <Container className="max-w-5xl">
          <Reveal>
            <div className="mb-8 max-w-2xl">
              <p className="eyebrow">Avant de nous écrire</p>
              <h2 className="h-section mt-3">Beaucoup de réponses sont déjà chiffrées.</h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
                Si votre besoin tient en un chiffre (combien d'aides, quel reste à charge, quelle
                prime), nos outils gratuits vous répondent en moins d'une minute, sans inscription.
              </p>
            </div>
          </Reveal>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SELF_SERVICE.map((tool, i) => {
              const Icon = tool.icon;
              const tone = TONE_STYLES[tool.tone];
              return (
                <Reveal key={tool.title} delay={i * 0.05}>
                  <a
                    href={tool.href}
                    className={cn(
                      'group relative flex h-full flex-col gap-3 rounded-2xl border bg-paper p-5 transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-floating',
                      tool.highlight ? 'border-accent-200/70' : 'border-stone-200',
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-xl ring-1 ring-inset',
                        tone.iconBg,
                        tone.ring,
                      )}
                    >
                      <Icon className={cn('h-[18px] w-[18px]', tone.iconText)} strokeWidth={1.7} />
                    </div>
                    <div>
                      <p className="font-display text-[15px] font-semibold text-ink">
                        {tool.title}
                      </p>
                      <p className="mt-1.5 text-[12.5px] leading-relaxed text-stone-600">
                        {tool.desc}
                      </p>
                    </div>
                    <div className="mt-auto inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-700">
                      Ouvrir l’outil
                      <ArrowUpRight
                        className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-0.5"
                        strokeWidth={2.2}
                      />
                    </div>
                    {tool.highlight && (
                      <Badge variant="accent" size="sm" className="absolute right-3 top-3">
                        Artisans RGE
                      </Badge>
                    )}
                  </a>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ─────────────── FAQ ─────────────── */}
      <section className="bg-paper py-16">
        <Container className="max-w-3xl">
          <Reveal>
            <div className="max-w-2xl">
              <p className="eyebrow">FAQ contact</p>
              <h2 className="h-section mt-3">Vos questions avant de nous écrire.</h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
                Sur les délais, la confidentialité, notre fonctionnement. Si la vôtre n’y figure
                pas, le formulaire est ouvert juste au-dessus.
              </p>
            </div>
          </Reveal>

          <div className="mt-8 space-y-3">
            {FAQ.map((item, i) => (
              <Reveal key={item.q} delay={i * 0.04}>
                <details className="group rounded-xl border border-stone-200 bg-stone-50/40 transition-colors open:border-brand-200 open:bg-brand-50/40">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5">
                    <span className="font-display text-[14.5px] font-semibold leading-tight text-ink">
                      {item.q}
                    </span>
                    <span
                      aria-hidden
                      className="relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-paper text-stone-500 transition-all duration-200 ease-out group-open:rotate-45 group-open:border-brand-300 group-open:bg-brand-50 group-open:text-brand-700"
                    >
                      <span className="block h-3 w-px bg-current" />
                      <span className="absolute block h-px w-3 bg-current" />
                    </span>
                  </summary>
                  <div className="-mt-1 px-5 pb-5 text-[13.5px] leading-relaxed text-stone-600">
                    {item.a}
                  </div>
                </details>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Contact', url: 'https://solutionecofrance.fr/contact' },
        ]}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// Sous-composants
// ════════════════════════════════════════════════════════════════

interface StepShellProps {
  direction: 1 | -1;
  reducedMotion: boolean;
  children: React.ReactNode;
}

function StepShell({ direction, reducedMotion, children }: StepShellProps) {
  if (reducedMotion) {
    return <div className="min-h-[380px]">{children}</div>;
  }
  return (
    <motion.div
      custom={direction}
      initial={{ opacity: 0, x: direction * 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: direction * -24 }}
      transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
      className="min-h-[380px]"
    >
      {children}
    </motion.div>
  );
}

function StepHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="max-w-2xl">
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="mt-2 font-display text-h-pillar font-bold tracking-tight text-ink">{title}</h2>
      {subtitle && <p className="mt-2 text-[13.5px] leading-relaxed text-stone-600">{subtitle}</p>}
    </div>
  );
}

interface FieldTextProps {
  label: string;
  name: string;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  hint?: string;
  error?: string;
  className?: string;
}

function FieldText({
  label,
  name,
  type = 'text',
  autoComplete,
  required,
  value,
  onChange,
  disabled,
  hint,
  error,
  className,
}: FieldTextProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={`field-${name}`}
        className="flex items-center gap-1 text-[13px] font-medium text-ink"
      >
        {label}
        {required && <span className="text-brand-600">*</span>}
      </label>
      <input
        id={`field-${name}`}
        name={name}
        type={type}
        autoComplete={autoComplete}
        required={required}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        className={cn(
          'h-10 w-full rounded-xl border bg-paper px-3 text-[14px] text-ink transition-colors placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60',
          error
            ? 'border-red-300 focus-visible:border-red-400 focus-visible:ring-red-500/20'
            : 'border-stone-200 focus-visible:border-brand-400 focus-visible:ring-brand-500/20',
        )}
      />
      {error ? (
        <p className="text-[11.5px] leading-snug text-red-600">{error}</p>
      ) : hint ? (
        <p className="text-[11.5px] leading-snug text-stone-500">{hint}</p>
      ) : null}
    </div>
  );
}

function Spinner() {
  return (
    <svg aria-hidden className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface SuccessPanelProps {
  leadId: string;
  audience: AudienceOption | null;
  intent: IntentOption | null;
  onReset: () => void;
}

function SuccessPanel({ leadId, audience, intent, onReset }: SuccessPanelProps) {
  const nextSteps = getNextSteps(audience, intent);
  return (
    <div className="relative">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100">
          <CheckCircle2 className="h-5 w-5 text-brand-700" strokeWidth={2} />
        </div>
        <div>
          <p className="font-display text-h-pillar font-bold text-ink">Message reçu.</p>
          <p className="text-[12.5px] text-stone-500">
            Référence #{leadId.slice(0, 8).toUpperCase()} · réponse sous 24 h ouvrées
          </p>
        </div>
      </div>

      <p className="mt-5 text-[14px] leading-relaxed text-stone-600">
        Un expert {audience ? audience.label.toLowerCase() : 'Solution Éco France'} vous recontacte
        sous 24 heures ouvrées (le plus souvent dans la demi-journée). En attendant&nbsp;:
      </p>

      <ul className="mt-5 space-y-2">
        {nextSteps.map((step) => (
          <li key={step.href}>
            <a
              href={step.href}
              className="group flex items-start justify-between gap-3 rounded-xl border border-stone-200 bg-paper p-4 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-elevated"
            >
              <div className="flex items-start gap-3">
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50">
                  <step.icon className="h-4 w-4 text-brand-700" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="font-display text-[14px] font-semibold text-ink">{step.label}</p>
                  <p className="mt-0.5 text-[12.5px] leading-snug text-stone-500">{step.sub}</p>
                </div>
              </div>
              <ArrowUpRight
                className="mt-1 h-4 w-4 shrink-0 text-stone-300 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-brand-700"
                strokeWidth={2}
              />
            </a>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="md" onClick={onReset}>
          Envoyer une autre demande
        </Button>
        <a
          href={ROUTES.home}
          className="text-[13px] font-medium text-stone-500 underline-offset-2 hover:text-ink hover:underline"
        >
          Retour à l’accueil
        </a>
      </div>
    </div>
  );
}

function getNextSteps(
  audience: AudienceOption | null,
  intent: IntentOption | null,
): Array<{ label: string; sub: string; href: string; icon: LucideIcon }> {
  if (audience?.id === 'artisan') {
    return [
      {
        label: 'Découvrir le fonctionnement',
        sub: 'Zéro avance, mandat en 5 minutes, paiement sous 30 à 60 jours.',
        href: ROUTES.artisans,
        icon: HardHat,
      },
      {
        label: 'Lire nos conditions générales',
        sub: 'Comment et combien sommes-nous rémunérés ?',
        href: ROUTES.legal.cgv,
        icon: ShieldCheck,
      },
    ];
  }
  if (audience?.id === 'entreprise' || audience?.id === 'collectivite') {
    return [
      {
        label: "Lancer l'audit obligation tertiaire",
        sub: 'Pré-étude offerte, plan de réduction chiffré.',
        href: ROUTES.decretTertiaire,
        icon: Gauge,
      },
      {
        label: 'Voir les cas similaires',
        sub: "Comment d'autres structures ont piloté leur stratégie énergie.",
        href: ROUTES.casClients,
        icon: FileSearch,
      },
    ];
  }
  if (intent?.id === 'comparer') {
    return [
      {
        label: "Lancer le comparateur d'énergie",
        sub: 'Tous les fournisseurs, aucun changement forcé.',
        href: ROUTES.comparateur,
        icon: GitCompareArrows,
      },
      {
        label: 'Voir nos références',
        sub: 'Cas concrets avec économies réelles documentées.',
        href: ROUTES.casClients,
        icon: FileSearch,
      },
    ];
  }
  if (
    intent?.id === 'renover' ||
    audience?.id === 'particulier' ||
    audience?.id === 'copropriete'
  ) {
    return [
      {
        label: 'Calculer mes aides rénovation',
        sub: 'Toutes les aides cumulées automatiquement.',
        href: ROUTES.renovation,
        icon: Calculator,
      },
      {
        label: 'Voir des rénovations réussies',
        sub: 'Cas concrets particuliers et copropriétés.',
        href: ROUTES.casClients,
        icon: FileSearch,
      },
    ];
  }
  return [
    {
      label: 'Lancer le diagnostic primes en 60 sec',
      sub: 'Estimation chiffrée immédiate, sans inscription.',
      href: ROUTES.diagnostic,
      icon: ScanLine,
    },
    {
      label: 'Explorer nos outils gratuits',
      sub: 'Calculateurs, comparateurs, simulateurs.',
      href: ROUTES.outils,
      icon: Sparkles,
    },
  ];
}

function getMessagePlaceholder(
  audience: AudienceOption | null,
  intent: IntentOption | null,
): string {
  if (audience?.id === 'artisan') {
    return 'Numéro de SIRET, qualifications actives, volume annuel estimé, primes déjà perçues…';
  }
  if (audience?.id === 'entreprise' || audience?.id === 'collectivite') {
    return 'Patrimoine concerné (nombre de sites, surfaces), enjeux principaux (obligation tertiaire, primes, courtage), échéances…';
  }
  if (audience?.id === 'copropriete') {
    return "Nombre de lots, étiquette énergie actuelle, travaux envisagés, date d'AG visée…";
  }
  if (audience?.id === 'agriculteur') {
    return "Type d'exploitation, surface des bâtiments, équipements à remplacer ou à isoler…";
  }
  if (intent?.id === 'comparer') {
    return 'Vos consommations annuelles (gaz / élec), type de site, échéance de votre contrat actuel…';
  }
  if (intent?.id === 'renover') {
    return 'Type de logement, année de construction, travaux envisagés, situation fiscale…';
  }
  return 'Décrivez votre situation en quelques lignes — plus c’est précis, plus on peut être utile dès la première réponse.';
}
